import { VesselArea } from '@seaber/common/graphql/types';
import { isDefined } from '@seaber/common/lib/generics';
import { useCallback } from 'react';
import { useBunkerGradeOverrides } from '../components/hooks/useBunkerPrices';
import { mapValues } from 'lodash';
import { useCurrentUser } from '../state/zustand';

export type OrgDefaultBunkerGradeIdsByArea = {
  [VesselArea.INSIDE_ECA]?: number | null;
  [VesselArea.OUTSIDE_ECA]?: number | null;
};

export type OrgDefaultBunkerGradeArea = keyof OrgDefaultBunkerGradeIdsByArea;

export function isOrgDefaultBunkerGradeArea(
  area?: string,
): area is OrgDefaultBunkerGradeArea {
  return area === VesselArea.INSIDE_ECA || area === VesselArea.OUTSIDE_ECA;
}

export function isDefaultOrgGradeForArea({
  area,
  orgDefaultBunkerGradeNamesByArea,
  gradeName,
}: {
  area?: VesselArea;
  orgDefaultBunkerGradeNamesByArea?: Partial<
    Record<OrgDefaultBunkerGradeArea, string | undefined>
  >;
  gradeName?: string;
}) {
  if (!isOrgDefaultBunkerGradeArea(area)) return false;

  const orgDefaultBunkerGradeName = orgDefaultBunkerGradeNamesByArea?.[area];
  return orgDefaultBunkerGradeName === gradeName;
}

export function useIsDefaultOrgGradeForArea() {
  const {
    parsedBunkerGradeOverrides: { bunkerGradeOverrides },
  } = useBunkerGradeOverrides();
  const orgDefaultBunkerGradeNamesByArea = mapValues(
    transformAreaBunkerGradeMappings(
      useCurrentUser()?.organization
        ?.organizationAreaBunkerGradeMappingsByOrganizationId?.nodes,
    ),
    (value) => bunkerGradeOverrides.find((bgo) => bgo.id === value)?.gradeName,
  );

  return useCallback(
    (area?: VesselArea, gradeName?: string) => {
      return isDefaultOrgGradeForArea({
        area,
        orgDefaultBunkerGradeNamesByArea,
        gradeName,
      });
    },
    [orgDefaultBunkerGradeNamesByArea],
  );
}

type TransformAreaBunkerGradeMappingsResult<T extends boolean> = T extends true
  ? Record<OrgDefaultBunkerGradeArea, string>
  : OrgDefaultBunkerGradeIdsByArea;

export function transformAreaBunkerGradeMappings<T extends boolean = false>(
  mappings?: ({
    vesselArea: VesselArea;
    bunkerGradeId: number;
  } | null)[],
  gradeIdAsString?: T,
): TransformAreaBunkerGradeMappingsResult<T> {
  if (!mappings?.length) return {} as TransformAreaBunkerGradeMappingsResult<T>;

  const convertToString = gradeIdAsString ?? false;

  return Object.fromEntries(
    mappings.filter(isDefined).map((mapping) => {
      const gradeId = convertToString
        ? String(mapping.bunkerGradeId)
        : mapping.bunkerGradeId;
      return [mapping.vesselArea, gradeId];
    }),
  ) as TransformAreaBunkerGradeMappingsResult<T>;
}
